import React from 'react';
import { useNavigate } from 'react-router-dom';
import AppActionMenu from '../../../../components/AppActionMenu';

export default function AttendanceRulesTable({
  records = [],
  isLoading = false,
  onArchive,
  onDelete,
  onResetFilters,
}) {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-12 text-center shadow-sm">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          <p className="text-xs font-semibold text-on-surface-variant">Loading Attendance Rules...</p>
        </div>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-12 text-center shadow-sm">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
            <span className="material-symbols-outlined text-3xl">rule</span>
          </div>
          <h3 className="text-lg font-bold text-on-surface">No Attendance Rules Found</h3>
          <p className="text-xs text-on-surface-variant max-w-md">
            There are no rules matching your filter criteria.
          </p>
          {onResetFilters && (
            <button
              type="button"
              onClick={onResetFilters}
              className="mt-2 rounded-xl bg-primary px-5 py-2 text-xs font-bold text-on-primary shadow-sm hover:bg-primary/90 transition cursor-pointer"
            >
              Reset Filters
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1050px] text-left text-sm">
          <thead>
            <tr className="border-b border-outline-variant/20 bg-surface-container-low/60 text-xs font-bold uppercase tracking-[0.08em] text-on-surface-variant">
              <th className="px-5 py-3.5">Rule Name</th>
              <th className="px-5 py-3.5">Alias</th>
              <th className="px-4 py-3.5 text-center">Swipe After</th>
              <th className="px-4 py-3.5 text-center">Swipe Before</th>
              <th className="px-4 py-3.5 text-center">Late Cut</th>
              <th className="px-4 py-3.5 text-center">Early Cut</th>
              <th className="px-4 py-3.5 text-center">Late Penalty</th>
              <th className="px-4 py-3.5 text-center">Early Penalty</th>
              <th className="px-4 py-3.5 text-center">Sandwich</th>
              <th className="px-4 py-3.5 text-center">Status</th>
              <th className="w-20 px-5 py-3.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-variant/15">
            {records.map((item) => {
              const isInactive = item.status === 'Inactive';
              return (
                <tr
                  key={item.id}
                  onClick={() => navigate(`/payroll/attendance-rules/entry?id=${item.id}`)}
                  className="transition-colors hover:bg-surface-container-low/50 cursor-pointer group"
                >
                  {/* Rule Name */}
                  <td className="px-5 py-3.5 font-bold text-on-surface group-hover:text-primary transition-colors">
                    {item.ruleName}
                  </td>

                  {/* Alias */}
                  <td className="px-5 py-3.5 text-xs text-on-surface-variant font-medium">
                    {item.alias || '-'}
                  </td>

                  {/* SwipeTimeAfter */}
                  <td className="px-4 py-3.5 text-center text-xs font-medium text-on-surface">
                    {item.swipeTimeAfter}m
                  </td>

                  {/* SwipeTimeBefore */}
                  <td className="px-4 py-3.5 text-center text-xs font-medium text-on-surface">
                    {item.swipeTimeBefore}m
                  </td>

                  {/* LateHalfDayDeduct */}
                  <td className="px-4 py-3.5 text-center text-xs font-medium text-on-surface">
                    {item.lateHalfDayDeduct > 0 ? `${item.lateHalfDayDeduct}m` : '-'}
                  </td>

                  {/* EarlyHalfDayDeduct */}
                  <td className="px-4 py-3.5 text-center text-xs font-medium text-on-surface">
                    {item.earlyHalfDayDeduct > 0 ? `${item.earlyHalfDayDeduct}m` : '-'}
                  </td>

                  {/* Late Penalty */}
                  <td className="px-4 py-3.5 text-center text-xs font-medium text-on-surface">
                    {item.noOfLateEarlyMarks > 0 ? (
                      <span className="inline-flex items-center gap-1 rounded-md bg-amber-500/10 px-2 py-0.5 text-[11px] font-semibold text-amber-600 dark:text-amber-400">
                        {item.noOfLateEarlyMarks} marks &rarr; {item.lateEarlyDaysDeduct}d
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>

                  {/* Early Penalty */}
                  <td className="px-4 py-3.5 text-center text-xs font-medium text-on-surface">
                    {item.earlyGoHf > 0 ? (
                      <span className="inline-flex items-center gap-1 rounded-md bg-blue-500/10 px-2 py-0.5 text-[11px] font-semibold text-blue-600 dark:text-blue-400">
                        {item.earlyGoHf} marks &rarr; {item.earlyGoDaysDeduct}d
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>

                  {/* Sandwich */}
                  <td className="px-4 py-3.5 text-center text-xs font-medium text-on-surface">
                    {item.sandwitchPresent ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-bold text-primary">
                        Yes
                      </span>
                    ) : (
                      <span className="text-on-surface-variant text-[11px]">No</span>
                    )}
                  </td>

                  {/* Status Badge */}
                  <td className="px-4 py-3.5 text-center">
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-bold ${
                        isInactive
                          ? 'bg-outline-variant/20 text-on-surface-variant'
                          : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                      }`}
                    >
                      <span
                        className={`h-1.5 w-1.5 rounded-full ${
                          isInactive ? 'bg-outline-variant' : 'bg-emerald-500'
                        }`}
                      />
                      {item.status || 'Active'}
                    </span>
                  </td>

                  {/* Actions Column with Edit and Archive/Unarchive */}
                  <td
                    className="px-5 py-3.5 text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <AppActionMenu
                      triggerLabel=""
                      triggerIcon="more_vert"
                      showChevron={false}
                      triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent cursor-pointer"
                      items={[
                        { key: 'edit', label: 'Edit Rule', icon: 'edit' },
                        {
                          key: 'archive',
                          label: isInactive ? 'Unarchive' : 'Archive',
                          icon: isInactive ? 'unarchive' : 'archive',
                        },
                        {
                          key: 'delete',
                          label: 'Delete Rule',
                          icon: 'delete',
                          danger: true,
                        },
                      ]}
                      onSelect={(key) => {
                        if (key === 'edit') {
                          navigate(`/payroll/attendance-rules/entry?id=${item.id}`);
                        } else if (key === 'archive') {
                          onArchive?.(item.id, item.ruleName, item.status);
                        } else if (key === 'delete') {
                          onDelete?.(item.id, item.ruleName);
                        }
                      }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
